"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import { SignInForm } from "./sign-in-form";
import { SignUpForm } from "./sign-up-form";
import { SetPasswordForm } from "./set-password-form";
import { Button } from "@garagem/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@garagem/ui/components/card";
import { api } from "@/lib/api/client";

/** Pull the invitation token out of the `from=/aceitar-convite?token=…` param. */
function extractTokenFromReturnUrl(from: string | null | undefined): string | null {
  if (!from) return null;
  const query = from.split("?")[1];
  if (!query) return null;
  try {
    return new URLSearchParams(query).get("token");
  } catch {
    return null;
  }
}

export function InvitationAuthForm() {
  const searchParams = useSearchParams();
  const emailFromUrl = searchParams?.get("email");
  const fromParam = searchParams?.get("from");
  const isInvitationFlow = !!(
    searchParams?.get("invitation") === "true" ||
    fromParam?.includes("aceitar-convite")
  );

  const token =
    searchParams?.get("token") || extractTokenFromReturnUrl(fromParam);

  // Token-gated lookup: tells us whether the invitee already has an account so
  // we can default returning users to the "Entrar" tab (signup would just fail
  // with USER_ALREADY_EXISTS).
  const { data: invitationDetails } = api.auth.getInvitationDetails.useQuery(
    { token: token as string },
    { enabled: isInvitationFlow && !!token, staleTime: 60_000 }
  );
  const inviteeHasAccount =
    (invitationDetails as { inviteeHasAccount?: boolean } | undefined)
      ?.inviteeHasAccount === true;
  // Invitee has a `user` row but no password credential (typical for
  // bulk-imported members). They can neither sign in (no password) nor sign up
  // (USER_ALREADY_EXISTS), so we route them to a dedicated "set password" step.
  const inviteeNeedsPassword =
    inviteeHasAccount &&
    (invitationDetails as { inviteeHasPassword?: boolean } | undefined)
      ?.inviteeHasPassword === false;

  const [mode, setMode] = useState<"signin" | "signup" | "setpassword">(
    isInvitationFlow ? "signup" : "signin"
  );

  // Once we learn the invitee's account state, land them on the right step
  // (unless they've already picked one themselves):
  //  - account + password       → sign in
  //  - account, no password     → set password
  //  - no account               → sign up (the invitation default)
  const userChoseMode = useRef(false);
  const autoApplied = useRef(false);
  useEffect(() => {
    if (!isInvitationFlow || userChoseMode.current || autoApplied.current) return;
    if (invitationDetails === undefined) return; // wait for the token lookup
    if (inviteeNeedsPassword) {
      setMode("setpassword");
      autoApplied.current = true;
    } else if (inviteeHasAccount) {
      setMode("signin");
      autoApplied.current = true;
    }
  }, [inviteeHasAccount, inviteeNeedsPassword, isInvitationFlow, invitationDetails]);

  const chooseMode = (next: "signin" | "signup" | "setpassword") => {
    userChoseMode.current = true;
    setMode(next);
  };

  // Only render this component for invitation flows
  if (!isInvitationFlow) {
    return <SignInForm />;
  }

  let description: ReactNode;
  if (mode === "setpassword") {
    description = (
      <>
        Defina uma senha
        {emailFromUrl ? (
          <>
            {" "}para{" "}
            <strong className="font-semibold">{emailFromUrl}</strong>
          </>
        ) : null}{" "}
        para acessar sua conta.
      </>
    );
  } else if (inviteeHasAccount) {
    description = (
      <>
        Você já tem uma conta
        {emailFromUrl ? (
          <>
            {" "}com o email{" "}
            <strong className="font-semibold">{emailFromUrl}</strong>
          </>
        ) : null}
        . Entre com sua senha para aceitar o convite.
      </>
    );
  } else if (emailFromUrl) {
    description = (
      <>
        Acesse sua conta com o email{" "}
        <strong className="font-semibold">{emailFromUrl}</strong> ou crie uma
        nova conta
      </>
    );
  } else {
    description = "Acesse sua conta ou crie uma nova conta";
  }

  return (
    <div className="w-full max-w-md space-y-4">
      {/* Invitation Context Banner */}
      <Card className="w-full max-w-md bg-gradient-to-br from-[#E2D9C9] to-[#BFE0FF] !border-0 shadow-none overflow-hidden">
        <CardHeader>
          <CardTitle className="text-center text-xl font-bold text-black mb-2">
            Aceitar convite
          </CardTitle>
          <CardDescription className="text-center text-black/80">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {mode === "setpassword" ? (
            <p className="text-center text-xs text-black/70">
              Já definiu uma senha?{" "}
              <button
                type="button"
                onClick={() => chooseMode("signin")}
                className="underline font-semibold"
              >
                Entrar
              </button>
            </p>
          ) : (
            <>
              {/* Login/Signup Toggle */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-white/50 rounded-lg">
                <Button
                  variant={mode === "signin" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => chooseMode("signin")}
                  className={
                    mode === "signin"
                      ? "shadow-sm bg-black text-white hover:bg-black/90"
                      : "text-black hover:bg-white/70"
                  }
                >
                  Entrar
                </Button>
                <Button
                  variant={mode === "signup" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => chooseMode("signup")}
                  className={
                    mode === "signup"
                      ? "shadow-sm bg-black text-white hover:bg-black/90"
                      : "text-black hover:bg-white/70"
                  }
                >
                  Criar Conta
                </Button>
              </div>
              {inviteeHasAccount && mode === "signup" && (
                <p className="text-center text-xs text-black/70">
                  Já existe uma conta com este email. Use{" "}
                  <strong className="font-semibold">Entrar</strong> e, se esqueceu
                  a senha, &quot;Esqueceu sua senha?&quot;.
                </p>
              )}
              {mode === "signin" && inviteeNeedsPassword && (
                <p className="text-center text-xs text-black/70">
                  Ainda não definiu uma senha?{" "}
                  <button
                    type="button"
                    onClick={() => chooseMode("setpassword")}
                    className="underline font-semibold"
                  >
                    Definir senha
                  </button>
                </p>
              )}
              {mode === "signin" &&
                (invitationDetails as { inviteeHasPassword?: boolean } | undefined)
                  ?.inviteeHasPassword === true && (
                  <p className="text-center text-xs text-black/70">
                    Não lembra sua senha?{" "}
                    <a
                      href={`/esqueci-senha${
                        emailFromUrl
                          ? `?email=${encodeURIComponent(emailFromUrl)}`
                          : ""
                      }`}
                      className="underline font-semibold"
                    >
                      Redefinir senha
                    </a>
                  </p>
                )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Render the appropriate form */}
      {mode === "setpassword" ? (
        <SetPasswordForm
          token={(token as string) ?? ""}
          email={emailFromUrl}
          onAlreadyHasPassword={() => chooseMode("signin")}
          onNeedsSignup={() => chooseMode("signup")}
        />
      ) : mode === "signin" ? (
        <SignInForm />
      ) : (
        // If signup fails because the account already exists, route to the
        // right step: "Definir senha" when they have no password (avoids
        // recreating the deadlock), otherwise "Entrar".
        <SignUpForm
          onAccountExists={() =>
            chooseMode(inviteeNeedsPassword ? "setpassword" : "signin")
          }
        />
      )}
    </div>
  );
}
